import {useRef} from 'react';

import {Alert, SimpleGrid} from '@mantine/core';
import {I18n} from '@aws-amplify/core';
import {useNavigate} from 'react-router-dom';

import {useBillableEvents} from '../hooks/useBillableEvents.ts';
import {useBillableEventsSearch} from '../hooks/useBillableEventsSearch.ts';
import {useBillableEventsTableColumns} from '../hooks/useBillableEventsTableColumns.tsx';
import {BillableEvent} from '../types.ts';
import {moduleConfig} from '../config';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

export const RecordsTable = () => {
    const {columns} = useBillableEventsTableColumns();
    const navigate = useNavigate();

    const {
        // The loaded set BEFORE keyword filtering: what the footer's "N of M loaded"
        // claim is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
    } = useBillableEvents();

    // Same module-scoped store the list hook filters on: this component owns the input,
    // useBillableEvents owns the filtering.
    const {keyword, handleFilterByKeyword} = useBillableEventsSearch();
    const term = keyword.trim();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Read-only (FNP-533): no selection / no edit drawer. Requeue and Write off are per-row
    // actions with no batch form. Row click opens the detail view. The BillableEvent id is
    // a COMPOSITE key (`billable-event:metering-record:<uuid>#meter:<code>#<iso>#_`)
    // containing `#`, `:` and `.`, so it MUST be encodeURIComponent'd: a raw `#` is a URL
    // fragment delimiter and would truncate the path, leaving the detail page on its
    // loader forever. react-router v6 useParams() auto-decodes.
    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load billable events')}>
                    {I18n.get('billing-api did not return the list. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<BillableEvent>
                columns={columns}
                records={visibleItems}
                // Client-side, over the pages loaded so far: the same scope the keyword
                // filter works on.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) => navigate(`${moduleConfig.basePath}/${encodeURIComponent(record.id)}`)}
            />

            {/* Two empty states, not one (§3): "no events exist" and "the search matched
                nothing" mean opposite things. Gated on !loading so it does not flash on the
                first fetch, and on !isError so a failed load keeps its own Alert. */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert
                    color={term ? 'gray' : 'blue'}
                    title={term ? I18n.get('No matching billable events') : I18n.get('No billable events')}
                >
                    {term
                        ? `${I18n.get('Nothing in the loaded rows matches')} "${term}". ${I18n.get('Clear the search to see every loaded row. A match may still be on a page that has not been loaded yet.')}`
                        : I18n.get('No billable event matches the current filters. Events appear here when a closed metering record is emitted to a billing channel.')}
                </Alert>
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'billable event'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
